import React from "react";
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  InputAdornment,
  TextField,
  Select,
  MenuItem,
  Button,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { LocationScenarioSectionsModel } from "pages/Dashboards/ScenarioView/data/LocationScenarioSectionsModel";

interface LocationItem {
  locationId: number;
  locationName: string;
}

export interface DecarbPlansDrawerProps {
  open: boolean;
  onClose: () => void;
  onApply: (selections: Record<number, string>, metadata?: DecarbPlansApplyMetadata) => void;
  locationSections: LocationScenarioSectionsModel[];
  locationsList: LocationItem[];
  appliedSelections: Record<number, string>;
}

export interface DecarbPlansApplyMetadata {
  requestedSection?: string;
  unavailableLocationIds?: number[];
}

const getDefaultSection = (sections: string[]): string => sections[0] ?? "";

const areSelectionsEqual = (
  first: Record<number, string>,
  second: Record<number, string>
): boolean => {
  const firstKeys = Object.keys(first);
  const secondKeys = Object.keys(second);

  return (
    firstKeys.length === secondKeys.length &&
    firstKeys.every((key) => first[Number(key)] === second[Number(key)])
  );
};

const getUnavailableLocationIds = (
  locationSections: LocationScenarioSectionsModel[],
  requestedSection?: string
): number[] => {
  if (!requestedSection) {
    return [];
  }

  return locationSections
    .filter(
      (ls) => !ls.scenarioSections.some((section) => section.trim() === requestedSection)
    )
    .map((ls) => ls.locationId);
};

const DecarbPlansDrawer: React.FC<DecarbPlansDrawerProps> = ({
  open,
  onClose,
  onApply,
  locationSections,
  locationsList,
  appliedSelections,
}) => {
  const [search, setSearch] = React.useState("");
  const [tempSelections, setTempSelections] = React.useState<Record<number, string>>({});
  const [bulkSection, setBulkSection] = React.useState("");
  const [bulkApplyMetadata, setBulkApplyMetadata] = React.useState<DecarbPlansApplyMetadata | null>(null);
  const wasOpenRef = React.useRef(false);

  /* Sections appearing in more than one location's options (trimmed to avoid whitespace duplicates) */
  const sharedSections = React.useMemo(() => {
    const counts = new Map<string, number>();
    locationSections.forEach((ls) =>
      ls.scenarioSections.forEach((s) => {
        const key = s.trim();
        counts.set(key, (counts.get(key) ?? 0) + 1);
      })
    );
    return Array.from(counts.entries())
      .filter(([, count]) => count > 1)
      .map(([s]) => s);
  }, [locationSections]);

  /* Initialize on open, then merge parent updates without wiping in-progress edits. */
  React.useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return;
    }

    const isOpening = !wasOpenRef.current;
    wasOpenRef.current = true;

    setTempSelections((prev) => {
      const next: Record<number, string> = {};
      locationSections.forEach((ls) => {
        const currentSelection = prev[ls.locationId];
        next[ls.locationId] =
          currentSelection && ls.scenarioSections.includes(currentSelection)
            ? currentSelection
            : appliedSelections[ls.locationId] ?? getDefaultSection(ls.scenarioSections);
      });

      return areSelectionsEqual(prev, next) ? prev : next;
    });

    if (isOpening) {
      setSearch("");
      setBulkSection("");
      setBulkApplyMetadata(null);
    }
  }, [open, locationSections, appliedSelections]);

  const locationNameMap = React.useMemo(
    () => new Map(locationsList.map((l) => [l.locationId, l.locationName])),
    [locationsList]
  );

  const getLocationName = (locationId: number): string =>
    locationNameMap.get(locationId) ?? `Location ${locationId}`;

  const isCustom = (ls: LocationScenarioSectionsModel): boolean =>
    tempSelections[ls.locationId] !== getDefaultSection(ls.scenarioSections);

  const handleResetDefaults = () => {
    const reset: Record<number, string> = {};
    locationSections.forEach((ls) => {
      reset[ls.locationId] = getDefaultSection(ls.scenarioSections);
    });
    setTempSelections(reset);
    setBulkApplyMetadata(null);
  };

  const handleApplyBulk = () => {
    if (!bulkSection) return;

    setTempSelections((prev) => {
      const next = { ...prev };
      locationSections.forEach((ls) => {
        const match = ls.scenarioSections.find((s) => s.trim() === bulkSection);
        if (match !== undefined) {
          next[ls.locationId] = match;
        }
      });
      return next;
    });

    setBulkApplyMetadata({
      requestedSection: bulkSection,
      unavailableLocationIds: getUnavailableLocationIds(locationSections, bulkSection),
    });
  };

  const handleSetPlans = () => {
    if (!bulkApplyMetadata?.requestedSection) {
      onApply(tempSelections);
      return;
    }

    onApply(tempSelections, {
      requestedSection: bulkApplyMetadata.requestedSection,
      unavailableLocationIds: getUnavailableLocationIds(
        locationSections,
        bulkApplyMetadata.requestedSection
      ),
    });
  };

  const filteredSections = locationSections.filter((ls) =>
    getLocationName(ls.locationId).toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 340,
          height: "100vh",
          top: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          pt: 1.5,
          pb: 0.5,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "0.88rem", color: "#0f2b52" }}>
          Decarbonization Plans
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: "#64748b", p: 0.5 }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <Typography sx={{ px: 2, pb: 1, fontSize: "0.75rem", color: "#64748b" }}>
        Select one plan per location for portfolio calculations.
      </Typography>

      <Divider />

      {/* Bulk-apply: sections shared across more than one location */}
      {sharedSections.length > 0 && (
        <Box sx={{ px: 2, py: 1.25, backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "#475569", mb: 0.75 }}>
            Single section to apply on all
          </Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Select
              size="small"
              displayEmpty
              value={bulkSection}
              onChange={(e) => setBulkSection(e.target.value)}
              sx={{
                flex: 1,
                minWidth: 0,
                borderRadius: "6px",
                fontSize: "0.78rem",
                backgroundColor: "#fff",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
                "& .MuiSelect-select": {
                  py: 0.6,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: { "& .MuiMenuItem-root": { fontSize: "0.78rem", minHeight: 30, py: 0.4 } },
                },
              }}
            >
              <MenuItem value="" disabled sx={{ fontSize: "0.78rem" }}>
                Select a section…
              </MenuItem>
              {sharedSections.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
            <Button
              variant="contained"
              size="small"
              disabled={!bulkSection}
              onClick={handleApplyBulk}
              sx={{
                textTransform: "none",
                fontSize: "0.75rem",
                whiteSpace: "nowrap",
                flexShrink: 0,
                backgroundColor: "#E57A1F",
                boxShadow: "none",
                py: 0.55,
                "&:hover": { backgroundColor: "#cf6915", boxShadow: "none" },
                "&.Mui-disabled": { backgroundColor: "#e2e8f0", color: "#94a3b8" },
              }}
            >
              Apply
            </Button>
          </Box>
          {bulkApplyMetadata?.requestedSection && (bulkApplyMetadata.unavailableLocationIds?.length ?? 0) > 0 && (
            <Typography sx={{ mt: 0.75, fontSize: "0.72rem", color: "#92400e", lineHeight: 1.35 }}>
              {bulkApplyMetadata.requestedSection} is not available for every selected location. Those locations will use their first available plan.
            </Typography>
          )}
        </Box>
      )}

      {/* Search */}
      <Box sx={{ px: 2, py: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search locations..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 15, color: "#94a3b8" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "6px",
              fontSize: "0.78rem",
              "& fieldset": { borderColor: "#d1d5db" },
            },
          }}
        />
      </Box>

      {/* Location List */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 2 }}>
        {filteredSections.length === 0 && (
          <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8", py: 1.5, textAlign: "center" }}>
            No locations found.
          </Typography>
        )}
        {filteredSections.map((ls) => {
          const custom = isCustom(ls);
          const locationName = getLocationName(ls.locationId);
          const selected = tempSelections[ls.locationId] ?? getDefaultSection(ls.scenarioSections);

          return (
            <Box key={ls.locationId} sx={{ mb: 1.25 }}>
              {/* Location name row */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: "#E57A1F",
                      flexShrink: 0,
                    }}
                  />
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#0f2b52" }}>
                    {locationName}
                  </Typography>
                </Box>
                {custom ? (
                  <Typography sx={{ fontSize: "0.70rem", fontWeight: 600, color: "#E57A1F" }}>
                    Custom
                  </Typography>
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                    <Typography sx={{ fontSize: "0.70rem", color: "#94a3b8" }}>Default</Typography>
                    <KeyboardArrowDownIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
                  </Box>
                )}
              </Box>

              {/* Section dropdown */}
              <Select
                size="small"
                fullWidth
                value={selected}
                onChange={(e) =>
                  setTempSelections((prev) => ({ ...prev, [ls.locationId]: e.target.value }))
                }
                sx={{
                  borderRadius: "6px",
                  fontSize: "0.78rem",
                  backgroundColor: "#fff",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
                  "& .MuiSelect-select": { py: 0.6 },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: { "& .MuiMenuItem-root": { fontSize: "0.78rem", minHeight: 30, py: 0.4 } },
                  },
                }}
              >
                {ls.scenarioSections.map((section) => (
                  <MenuItem key={section} value={section}>
                    {section}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          );
        })}
      </Box>

      {/* Footer */}
      <Divider />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.25,
          gap: 1,
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={handleResetDefaults}
          sx={{
            background: "none",
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            color: "#64748b",
            fontSize: "0.75rem",
            p: 0,
            "&:hover": { color: "#0f2b52" },
          }}
        >
          ↺ Reset to defaults
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            sx={{
              textTransform: "none",
              fontSize: "0.75rem",
              borderColor: "#d1d5db",
              color: "#475569",
              py: 0.4,
              "&:hover": { borderColor: "#94a3b8" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSetPlans}
            sx={{
              textTransform: "none",
              fontSize: "0.75rem",
              backgroundColor: "#E57A1F",
              py: 0.4,
              boxShadow: "none",
              "&:hover": { backgroundColor: "#cf6915", boxShadow: "none" },
            }}
          >
            Set Plans
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default DecarbPlansDrawer;
